import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { buildAudienceFilterParams } from './audienceFilterParams';
import { buildCompanyAttributeOptions } from './companyAttributeOptions';
import { CycleInformation } from './components/CycleInformation';
import { mapEvaluationPeriodsToOptions } from './periodOptions';
import { useAutosaveCycleInformation } from './useAutosaveCycleInformation';
import { useCycleInformationFormSync } from './useCycleInformationFormSync';
import { useCycleInformationSubmit } from './useCycleInformationSubmit';
import {
    buildCycleInformationSchema,
    CycleInformationFormFields,
    isCycleInformationComplete,
    type CycleInformationFormData,
} from './validation';

import type { CycleAudienceTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';

import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { useGetEvaluationPeriods } from '@/api/cycle-evaluation-services/manager-assessment';
import {
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUserPermissions,
} from '@/api/directory';
import {
    cycleInformationNextBlockedAtom,
    cycleInformationOnNextAtom,
} from '@/atoms/cycleEvaluation';
import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useCompanyAttributesFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import { isNewCycleEvaluationGroupRoute } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { validationMessageKey } from '@/utils/validationMessage';

const AUDIENCE_COUNT_PAGE_SIZE = 1;

export const CycleInformationPage = () => {
    const { t } = useAmelioTranslations();
    const { cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const isNewCycle = isNewCycleEvaluationGroupRoute(cycleEvaluationGroupId);

    const {
        data: group,
        isError: groupError,
        isPending: isGroupPending,
    } = useGetCycleEvaluationGroup(isNewCycle ? undefined : cycleEvaluationGroupId);

    const {
        data: companyInformation,
        isError: companyInformationError,
        isPending: isCompanyInformationPending,
    } = useGetCompanyInformation();

    const {
        data: permissions,
        isError: permissionsError,
        isLoading: isPermissionsLoading,
    } = useGetUserPermissions();

    const { data: evaluationPeriodsRaw = [], isError: evaluationPeriodsError } =
        useGetEvaluationPeriods();

    const periods = useMemo(
        () => mapEvaluationPeriodsToOptions(evaluationPeriodsRaw),
        [evaluationPeriodsRaw],
    );

    const offeredPeriodValues = useMemo(() => periods.map(({ value }) => value), [periods]);

    const schema = useMemo(
        () => buildCycleInformationSchema(offeredPeriodValues),
        [offeredPeriodValues],
    );

    const {
        filters,
        selectedCompanyAttributes,
        onSelectCompanyAttribute,
        onSelectCompanyAttributeOption,
        setFilters,
        setSelectedCompanyAttributes,
    } = useCompanyAttributesFilters({
        company: companyInformation,
    });

    const {
        getValues,
        setValue,
        reset,
        trigger,
        watch,
        formState: { errors },
    } = useForm<CycleInformationFormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            [CycleInformationFormFields.name]: '',
            [CycleInformationFormFields.selectedPeriod]: '',
            [CycleInformationFormFields.audienceType]: CycleAudienceType.ENTIRE_COMPANY,
            [CycleInformationFormFields.resolvedEmployeeCount]: 0,
        },
    });

    const name = watch(CycleInformationFormFields.name);
    const selectedPeriod = watch(CycleInformationFormFields.selectedPeriod);
    const periodStart = watch(CycleInformationFormFields.periodStart);
    const periodEnd = watch(CycleInformationFormFields.periodEnd);
    const audienceType = watch(CycleInformationFormFields.audienceType);

    const filterParams = useMemo(
        () => buildAudienceFilterParams({ audienceType, filters }),
        [audienceType, filters],
    );

    const {
        data: companyPeopleData,
        isError: companyPeopleError,
        isPending: isCompanyPeoplePending,
    } = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: AUDIENCE_COUNT_PAGE_SIZE,
            isActiveOnly: true,
            ...filterParams,
        },
        {
            canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
        },
    );

    useFetchErrorsHandler({
        errors: [
            groupError,
            companyInformationError,
            permissionsError,
            evaluationPeriodsError,
            companyPeopleError,
        ],
    });

    const companyAttributes = useMemo(
        () => buildCompanyAttributeOptions(companyInformation),
        [companyInformation],
    );

    const resolvedEmployeeCount = companyPeopleData?.totalCount ?? 0;

    const { isAudienceHydrated } = useCycleInformationFormSync({
        isNewCycle,
        group,
        companyInformation,
        resolvedEmployeeCount,
        reset,
        setValue,
        setFilters,
        setSelectedCompanyAttributes,
    });

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);

    const draft = useMemo(
        () => ({ name, selectedPeriod, periodStart, periodEnd, audienceType, filters }),
        [name, selectedPeriod, periodStart, periodEnd, audienceType, filters],
    );

    const { markDraftChanged, markDraftSaved } = useAutosaveCycleInformation({
        isReadOnly,
        group,
        cycleEvaluationGroupId,
        draft,
        periods,
        offeredPeriodValues,
        isAudienceHydrated,
    });

    // The criteria are kept across a switch to the whole company: both the head-count query and
    // the saved payload already ignore them for that audience type, so clearing them here only
    // emptied the rows the user would come back to.
    const handleAudienceTypeChange = useCallback(
        (nextAudienceType: CycleAudienceTypeValue) => {
            markDraftChanged();
            setValue(CycleInformationFormFields.audienceType, nextAudienceType);
        },
        [markDraftChanged, setValue],
    );

    const submitCycleInformation = useCycleInformationSubmit({
        isReadOnly,
        isNewCycle,
        group,
        cycleEvaluationGroupId,
        trigger,
        getValues,
        periods,
        offeredPeriodValues,
        filters,
        isAudienceHydrated,
    });

    const onNext = useCallback(async () => {
        const advanced = await submitCycleInformation();

        if (advanced) {
            markDraftSaved(draft);
        }

        return advanced;
    }, [submitCycleInformation, markDraftSaved, draft]);

    useRegisterPageAction(cycleInformationOnNextAtom, onNext);

    const setNextBlocked = useSetAtom(cycleInformationNextBlockedAtom);

    useEffect(() => {
        setNextBlocked(
            !isReadOnly &&
                !isCycleInformationComplete({
                    name,
                    selectedPeriod,
                    periodStart,
                    periodEnd,
                    offeredPeriodValues,
                }),
        );

        return () => setNextBlocked(false);
    }, [
        setNextBlocked,
        isReadOnly,
        name,
        selectedPeriod,
        periodStart,
        periodEnd,
        offeredPeriodValues,
    ]);

    const isLoading =
        (!isNewCycle && isGroupPending) ||
        isCompanyInformationPending ||
        isPermissionsLoading ||
        isCompanyPeoplePending;

    const errorMessageOf = (message?: string) =>
        message ? t(validationMessageKey(message)) : undefined;

    return (
        <CycleInformation
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            isAudienceHydrated={isAudienceHydrated}
            name={name}
            nameErrorMessage={errorMessageOf(errors.name?.message)}
            periodErrorMessage={errorMessageOf(errors.selectedPeriod?.message)}
            audienceErrorMessage={errorMessageOf(errors.resolvedEmployeeCount?.message)}
            selectedPeriod={selectedPeriod}
            periods={periods}
            periodStart={periodStart}
            periodEnd={periodEnd}
            audienceType={audienceType}
            companyAttributes={companyAttributes}
            selectedCompanyAttributes={selectedCompanyAttributes}
            filters={filters}
            resolvedEmployeeCount={resolvedEmployeeCount}
            onNameChange={(value) => {
                markDraftChanged();
                setValue(CycleInformationFormFields.name, value);
            }}
            onPeriodChange={(value) => {
                markDraftChanged();
                setValue(CycleInformationFormFields.selectedPeriod, value);
            }}
            onPeriodStartChange={(date) => {
                markDraftChanged();
                setValue(CycleInformationFormFields.periodStart, date);
            }}
            onPeriodEndChange={(date) => {
                markDraftChanged();
                setValue(CycleInformationFormFields.periodEnd, date);
            }}
            onAudienceTypeChange={handleAudienceTypeChange}
            onSelectCompanyAttribute={(value) => {
                markDraftChanged();
                onSelectCompanyAttribute(value);
            }}
            onSelectCompanyAttributeOption={(selection) => {
                markDraftChanged();
                onSelectCompanyAttributeOption(selection);
            }}
        />
    );
};
